





import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  MenuItem,
  Box,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CustomDialog from "../../CustomDialog";
import { deliveryAgentAPI } from "./deliveryAgentAPI";

const AddDeliveryAgent = ({ open, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    employeeId: '',
    agentRole: 'delivery',  // NEW FIELD
    vehicleType: 'bike',
    vehicleNumber: '',
    licenseNumber: '',
    pan_number: '',
    aadhaar_number: '',
    address: {
      street: '',
      city: '',
      state: '',
      pincode: ''
    },
    maxOrderCapacity: 5
  });

  const [errors, setErrors] = useState({});
  const [dialogState, setDialogState] = useState({
    open: false,
    type: '',
    message: ''
  });

  // NEW: Agent Role Types
  const agentRoles = [
    { value: 'delivery', label: 'Delivery Agent' },
    { value: 'sample_collection', label: 'Sample Collection Agent' }
  ];

  const vehicleTypes = [
    { value: 'bike', label: 'Bike' },
    { value: 'car', label: 'Car' },
    { value: 'van', label: 'Van' }
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
      // Phone validation (only 10 digits)
  if (name === "phone") {
    // Allow only numbers
    const numericValue = value.replace(/\D/g, "");

    // Limit to 10 digits
    if (numericValue.length > 10) return;

    setFormData((prev) => ({
      ...prev,
      [name]: numericValue,
    }));

    // Validation error
    setErrors((prev) => ({
      ...prev,
      phone:
        numericValue.length > 0 && numericValue.length < 10
          ? "Phone number must be 10 digits"
          : "",
    }));

    return;
  }

    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
  
    if (!formData.name.trim()) newErrors.name = 'Name is required';
    else if (!/^[A-Za-z\s]{2,}$/.test(formData.name.trim())) newErrors.name = 'Enter a valid name (letters only)';
  
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) newErrors.email = 'Invalid email format';
  
    // Phone: 10 digits, starts with 6-9
    if (!formData.phone.trim()) newErrors.phone = 'Phone is required';
    else if (!/^[6-9]\d{9}$/.test(formData.phone)) newErrors.phone = 'Enter a valid 10-digit Indian phone number';
  
    if (!formData.employeeId.trim()) newErrors.employeeId = 'Employee ID is required';
    if (!formData.agentRole) newErrors.agentRole = 'Agent role is required';
  
    // PAN: format ABCDE1234F
    if (!formData.pan_number.trim()) newErrors.pan_number = 'PAN number is required';
    else if (!/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(formData.pan_number.toUpperCase()))
      newErrors.pan_number = 'Invalid PAN format (e.g. ABCDE1234F)';
  
    // Aadhaar: exactly 12 digits
    if (!formData.aadhaar_number.trim()) newErrors.aadhaar_number = 'Aadhaar number is required';
    else if (!/^\d{12}$/.test(formData.aadhaar_number))
      newErrors.aadhaar_number = 'Aadhaar must be exactly 12 digits';
  
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
  
    try {
      const response = await deliveryAgentAPI.create(formData);
      if (response.data.success) {
        setDialogState({ open: true, type: 'success', message: 'Agent added successfully!' });
        setTimeout(() => { onSuccess(); handleClose(); }, 1500);
      }
    } catch (error) {
      // ✅ Show the specific backend message (PAN duplicate, Aadhaar duplicate, etc.)
      setDialogState({
        open: true,
        type: 'error',
        message: error.response?.data?.message || 'Failed to add agent'
      });
    }
  };

  const handleClose = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      pan_number: '',
      aadhaar_number: '',
      employeeId: '',
      agentRole: 'delivery',
      vehicleType: 'bike',
      vehicleNumber: '',
      licenseNumber: '',
      address: {
        street: '',
        city: '',
        state: '',
        pincode: ''
      },
      maxOrderCapacity: 5
    });
    setErrors({});
    onClose();
  };

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            Add Agent
            <IconButton onClick={handleClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent dividers>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Full Name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                error={!!errors.name}
                helperText={errors.name}
                required
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                error={!!errors.email}
                helperText={errors.email}
                required
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone Number"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                error={!!errors.phone}
                helperText={errors.phone}
                required
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Employee ID"
                name="employeeId"
                value={formData.employeeId}
                onChange={handleChange}
                error={!!errors.employeeId}
                helperText={errors.employeeId}
                required
              />
            </Grid>

            {/* NEW: Agent Role Selection */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label="Agent Role"
                name="agentRole"
                value={formData.agentRole}
                onChange={handleChange}
                error={!!errors.agentRole}
                helperText={errors.agentRole}
                required
              >
                {agentRoles.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="PAN Number"
              name="pan_number"
              value={formData.pan_number}
              onChange={(e) => {
                const value = e.target.value.toUpperCase();  // auto-uppercase
                if (value.length <= 10) handleChange({ target: { name: 'pan_number', value } });
              }}
              inputProps={{ maxLength: 10 }}
              error={!!errors.pan_number}
              helperText={errors.pan_number || 'Format: ABCDE1234F'}
              required
            />
            </Grid>

            <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Aadhaar Number"
              name="aadhaar_number"
              value={formData.aadhaar_number}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, '');  // digits only
                if (value.length <= 12) handleChange({ target: { name: 'aadhaar_number', value } });
              }}
              inputProps={{ maxLength: 12 }}
              error={!!errors.aadhaar_number}
              helperText={errors.aadhaar_number || '12-digit Aadhaar number'}
              required
            />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                select
                label="Vehicle Type"
                name="vehicleType"
                value={formData.vehicleType}
                onChange={handleChange}
                required
              >
                {vehicleTypes.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Vehicle Number"
                name="vehicleNumber"
                value={formData.vehicleNumber}
                onChange={handleChange}
                error={!!errors.vehicleNumber}
                helperText={errors.vehicleNumber}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="License Number"
                name="licenseNumber"
                value={formData.licenseNumber}
                onChange={handleChange}
                error={!!errors.licenseNumber}
                helperText={errors.licenseNumber}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Street Address"
                name="address.street"
                value={formData.address.street}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="City"
                name="address.city"
                value={formData.address.city}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="State"
                name="address.state"
                value={formData.address.state}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Pincode"
                name="address.pincode"
                value={formData.address.pincode}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Max Order Capacity"
                name="maxOrderCapacity"
                type="number"
                value={formData.maxOrderCapacity}
                onChange={handleChange}
                inputProps={{ min: 1, max: 20 }}
              />
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleClose} variant="outlined">
            Cancel
          </Button>
          <Button onClick={handleSubmit} variant="contained" sx={{ bgcolor: '#006D77' }}>
            Add Agent
          </Button>
        </DialogActions>
      </Dialog>

      <CustomDialog
        open={dialogState.open}
        type={dialogState.type}
        message={dialogState.message}
        onClose={() => setDialogState({ ...dialogState, open: false })}
      />
    </>
  );
};

export default AddDeliveryAgent;